import React, { useEffect, useState } from "react";

/**
 * Cycles through a list of image URLs with a smooth horizontal SLIDE every ~4s:
 * the incoming image slides in from the right while the previous image slides
 * out to the left — both moving in sync (~700ms, ease-in-out). The card's own
 * overflow:hidden clips them cleanly.
 *
 * Scoped to the Landing page hero collage — do NOT export or reuse elsewhere
 * without a fresh perf review (2 <img> layers + animation replay per tick).
 *
 * Props:
 *  - urls: string[]     (must have ≥1)
 *  - alt: string
 *  - intervalMs: number (default 4000; user spec: 3–5s)
 *  - jitterMs: number   (default 800; staggers cards so they don't slide in unison)
 *  - className: string  (applied to the outer wrapper — set aspect ratio here)
 */
export const ShuffleImage = ({ urls = [], alt = "", intervalMs = 4000, jitterMs = 800, className = "" }) => {
  const list = urls.filter(Boolean);
  const [step, setStep] = useState(0);
  const [firstReady, setFirstReady] = useState(false);

  // Preload the first (and next if available) URL so we never render a
  // half-loaded first frame — the shuffle only begins after the first image
  // is fully decoded by the browser.
  useEffect(() => {
    setFirstReady(false);
    if (!list.length) return;
    const first = new Image();
    first.decoding = "async";
    first.onload = () => setFirstReady(true);
    first.onerror = () => setFirstReady(true); // don't block forever on a bad URL
    first.src = list[0];
    // Warm up index 1 & 2 as well; ignored if not present.
    for (let i = 1; i < Math.min(3, list.length); i++) {
      const w = new Image();
      w.decoding = "async";
      w.src = list[i];
    }
  }, [list.length ? list[0] : null]); // eslint-disable-line

  useEffect(() => {
    if (list.length < 2 || !firstReady) return;
    const jitter = Math.floor(Math.random() * jitterMs);
    const id = setInterval(() => setStep((s) => s + 1), intervalMs + jitter);
    return () => clearInterval(id);
  }, [list.length, intervalMs, jitterMs, firstReady]);

  if (!list.length) return <div className={className} />;

  // Show a blank/skeleton panel until the first image is decoded — never a
  // placeholder image. Uses the same navy card colour so there's no perceived
  // flash before the real content appears.
  if (!firstReady) {
    return <div className={`relative h-full w-full overflow-hidden bg-[#0A1930] ${className}`} data-testid="shuffle-image-loading" />;
  }

  const cur = list[step % list.length];
  const prev = list[(step - 1 + list.length) % list.length];
  const slideEase = "cubic-bezier(0.4, 0, 0.2, 1)";

  return (
    <div className={`relative h-full w-full overflow-hidden ${className}`} data-testid="shuffle-image">
      <img
        key={`in-${step}`}
        src={cur}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ animation: `agSlideIn 700ms ${slideEase} both` }}
        loading="eager"
        draggable={false}
      />
      {step > 0 && (
        <img
          key={`out-${step}`}
          src={prev}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ animation: `agSlideOut 700ms ${slideEase} both` }}
          loading="eager"
          draggable={false}
        />
      )}
    </div>
  );
};
